import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useApp } from '../context/AppContext';

// Time left to order for next week. The deadline comes from the server
// (menuState.nextCutoff, an ISO timestamp with the Zurich offset on it) rather
// than being worked out in the browser — the cutoff day and hour are editable
// in settings, and a countdown that disagreed with the server about when
// ordering closes would be worse than no countdown at all.
function remainingMs(targetIso) {
  if (!targetIso) return null;
  const t = new Date(targetIso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, t - Date.now());
}

export default function PreorderCountdown({ compact = false }) {
  const { t, menuState } = useApp();
  const target = menuState?.nextCutoff;
  const [ms, setMs] = useState(() => remainingMs(target));

  useEffect(() => {
    setMs(remainingMs(target));
    if (!target) return undefined;
    const id = setInterval(() => setMs(remainingMs(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  // Closed: say when it comes back rather than counting down to nothing.
  if (menuState && menuState.open === false) {
    return (
      <span data-testid="preorder-countdown-closed"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-full whitespace-nowrap">
        <Clock size={13} /> {t('reopens_thu')}
      </span>
    );
  }

  if (ms === null || ms <= 0) return null;

  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, '0');

  // Coarse when the deadline is days away, to the second in the final hour —
  // the precision should match how much the customer needs to care.
  const value = d > 0
    ? `${d}${t('unit_d')} ${pad(h)}${t('unit_h')}`
    : h > 0
      ? `${h}${t('unit_h')} ${pad(m)}${t('unit_min')}`
      : `${m}${t('unit_min')} ${pad(sec)}s`;

  const urgent = s < 6 * 3600;

  return (
    <Link
      to="/?tab=week2"
      data-testid="preorder-countdown"
      title={t('order_next_week')}
      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
        urgent
          ? 'text-amber-800 bg-amber-50 border-amber-200 hover:bg-amber-100'
          : 'text-[#4b5e34] bg-[#eef0e6] border-[#d8dcc6] hover:bg-[#e4e8d6]'
      }`}
    >
      <Clock size={13} className="shrink-0" />
      {!compact && <span className="hidden md:inline">{t('closes_in')}</span>}
      <span className="tabular-nums font-semibold">{value}</span>
    </Link>
  );
}
